/**
 * algorithm-context.guard.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable, Type } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, CanDeactivate, Router }
  from "@angular/router";
import { Observable } from "rxjs/Rx";

import {
  AcknowledgmentResponse,
  BadReplyError,
  Message,
  MessageTag
} from "../server/messages/general/";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { Algorithm } from "../algorithms/structure/algorithm";
import { RunningAlgorithm } from "../algorithms/structure/running-algorithm";
import { AlertService } from "../common/alert.service";
import { SaveableForm } from "../intake-form/saveable-form";

/**
 * A conjunction type for routes with algorithm names in their parameters.
 */
interface AlgorithmParametered {
  params : {algorithm : string};
}

@Injectable()
export class AlgorithmRunningGuard
  implements CanActivate, CanDeactivate<SaveableForm>
{
  constructor (
    private router : Router,
    private alerts : AlertService,
    protected algorithms : AlgorithmService
  ) {/* DI */}

  protected hasParamAlgorithm (rt : ActivatedRouteSnapshot)
  : rt is ActivatedRouteSnapshot & AlgorithmParametered
  {
    return ("algorithm" in rt.params);
  }

  private algorithmAtLastActivate : RunningAlgorithm;

  /**
   * Ensure an algorithm is running and lock the algorithm until the component
   * is left.
   *
   * @return
   *        An Observable indicating whether deactivation is allowed.
   */
  public canActivate (route : ActivatedRouteSnapshot) : boolean
  {
    if (this.algorithms.isRunning
        && this.hasParamAlgorithm(route)
        && this.algorithms.running.algorithm.name === route.params.algorithm)
    {
      this.algorithmAtLastActivate = this.algorithms.running;
      document.body.className = this.algorithms.running.name || "";
      return true;
    }
    else
    {
      return false;
    }
  }

  /**
   * Unlock the algorithm and release it.
   *
   * @param component
   *        The {@link AlgorithmContext} being deactivated.
   * @return
   *        An Observable indicating whether deactivation is allowed.
   */
  public canDeactivate (component : SaveableForm) : Observable<boolean>
  {
    return (this.algorithms.release(this.algorithmAtLastActivate)
      .defaultIfEmpty(true)
      .do(() => {
        this.algorithmAtLastActivate = null;
        document.body.className = "a-none";
      }));
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/routing/algorithm-running.guard.ts